import { Middleware, ReducersMapObject } from 'redux'
import { StoreState } from '../root/reducer'
import { SagaIterator } from 'redux-saga'
import * as Api from 'mytours-read-api-typescript'
import STQRYBunnyReadApi from 'stqry-bunny-read-api'
import { AppInfo, Language } from '../settings/types'
import { Screen, AppMetaData, CollectionItem, Collection, DevicePlatform } from '../api/types'
import { FileType } from '../screen/types'
import { CollectionItemsContainerOwnProps } from '../tours/containers/tourstops'
import { CollectionListContainerOwnProps } from '../collectionlist/containers/collectionlist'
import { CouponCode } from '../lockapp/types'

export interface Configuration {
	/* Environment Variables */

	getDefaultApiConfiguration(): Api.Configuration

	getApiHeaders(): Record<string, string>

	isDemoMode(): boolean

	getDirectApiBaseUrl(region?: string): string

	getCdnApiBaseUrl(): string

	getDefaultAppInfo(): AppInfo

	getAccountId(): number

	shouldUsePublishedContent(): boolean

	stqryBunnyReadApi: STQRYBunnyReadApi

	/* Reducer/Sagas */

	customiseReduxMiddleware(middlewares: Middleware[]): Middleware[]

	customiseReducers(reducer: ReducersMapObject<StoreState>): ReducersMapObject<StoreState>

	getSagas(): SagaIterator

	/* Container Functions */

	// Needed a way to convert navigation props that are passed to containers via ownProps into the data the container requires
	convertCollectionItemsContainerOwnProps(props: any): CollectionItemsContainerOwnProps

	convertCollectionListContainerOwnProps(props: any): CollectionListContainerOwnProps

	/* Collection Functions */

	saveCollectionItemsToDisk(
		projectId: string,
		collectionId: number,
		collectionVersion: number,
		language: string,
		items: CollectionItem[] | undefined,
	): Promise<void>

	loadCollectionItemsFromDisk(
		projectId: string,
		collectionId: number,
		collectionVersion: number,
		language: string,
	): Promise<CollectionItem[] | undefined>

	removeCollectionItemsFromDisk(projectId: string, collectionId: number, language: string): Promise<void>

	saveCollectionDetailsToDisk(
		projectId: string,
		collectionId: number,
		collectionVersion: number,
		language: string,
		collection: Collection,
	): Promise<void>

	loadCollectionDetailsFromDisk(
		projectId: string,
		collectionId: number,
		collectionVersion: number,
		language: string,
	): Promise<Collection | undefined>

	removeCollectionDetailsFromDisk(projectId: string, collectionId: number, language: string): Promise<void>

	/* Screen Functions */

	loadScreenFromDisk(
		projectId: string,
		itemId: number,
		itemVersion: number,
		language: string,
	): Promise<Screen | undefined>

	saveScreenToDisk(
		projectId: string,
		itemId: number,
		itemVersion: number,
		language: string,
		item: Screen,
	): Promise<void>

	removeScreenFromDisk(projectId: string, itemId: number, language: string): Promise<void>

	/* Map route functions */

	saveMapRouteToDisk(
		projectId: string,
		mapRouteId: number,
		mapRouteVersion: number,
		language: string,
		mapRoute: any[],
	): Promise<void>

	loadMapRouteFromDisk(
		projectId: string,
		mapRouteId: number,
		mapRouteVersion: number,
		language: string,
	): Promise<any[] | undefined>

	removeMapRouteFromDisk(projectId: string, mapRouteId: number, language: string): Promise<void>

	/* Media Item Functions */

	loadMediaItemFromDisk(
		projectId: string,
		itemId: number,
		itemVersion: number,
		language: string,
	): Promise<Api.MediaItem | undefined>

	saveMediaItemToDisk(
		projectId: string,
		itemId: number,
		itemVersion: number,
		language: string,
		item: Api.MediaItem,
	): Promise<void>

	removeMediaItemFromDisk(projectId: string, itemId: number, language: string): Promise<void>

	checkFileExists(path: string): Promise<boolean>

	/* Form Functions */

	loadFormFromDisk(
		projectId: string,
		itemId: number,
		itemVersion: number,
		language: string,
	): Promise<Api.Form | undefined>

	saveFormToDisk(
		projectId: string,
		itemId: number,
		itemVersion: number,
		language: string,
		item: Api.Form,
	): Promise<void>

	removeFormFromDisk(projectId: string, itemId: number, language: string): Promise<void>

	/* Download File */

	downloadFile(type: FileType, fileName: string, url: string): Promise<string>

	getFileDownloadPath(type: FileType, fileName: string): string

	preDownloadImage(id: number, fileName: string, isForVideo: boolean): Promise<boolean>

	/* Settings Functions */

	saveAppMetaDataToDisk(projectId: string, data: AppMetaData): Promise<void>

	loadAppMetaDataFromDisk(projectId: string): Promise<AppMetaData | undefined>

	saveAppManifestToDisk(projectId: string, data: Api.Manifest): Promise<void>

	loadAppManifestFromDisk(projectId: string): Promise<Api.Manifest | undefined>

	handleAppUpgrade(projectId: string): Promise<boolean>

	changeLocalizationLanguage(languageCode: string): void

	changeTheme(metaData: AppMetaData): void

	getLanguages(): Language[]

	getPlatform(): DevicePlatform

	getDeviceUUID(): string

	/* Coupon Codes */

	saveCouponCodeToDisk(projectId: string, couponCode: CouponCode): Promise<void>

	loadCouponCodesFromDisk(projectId: string): Promise<CouponCode[] | undefined>

	removeCouponCodeFromDisk(projectId: string, couponCode: CouponCode): Promise<void>

	/* In-App Purchases */

	saveInAppPurchasesToDisk(appId: string, version: string, data: Api.InAppPurchase[]): Promise<void>

	loadInAppPurchasesDataFromDisk(appId: string, version: string): Promise<Api.InAppPurchase[] | undefined>
}

let configuration: Configuration

export function setConfiguration(config: Configuration): void {
	configuration = config
}

export function getConfiguration(): Configuration {
	if (configuration === undefined) {
		throw Error('Configuration has not been initialised')
	}
	return configuration
}
